import { derived, get, writable } from 'svelte/store';
import { edit, initialEdit } from 'src/stores/ui/edit_lists';
import { is_tablet } from 'src/stores/ui/viewport';
import { prefersReducedMotion } from 'src/helpers/ui_transitions';

/** True while the Exit-edit control is dissolving before tabs restore. */
export const editChromeExitDissolving = writable(false);

/** Matches Tabs `.is-exit-dissolving` opacity/transform duration. */
export const EDIT_EXIT_DISSOLVE_MS = 200;

/**
 * Peeking-tabs edit chrome on mobile and tablet (both shift list items while
 * reordering). Desktop keeps the full tab rail.
 */
export const compactEditChrome = derived(
	[edit, editChromeExitDissolving, is_tablet],
	([$edit, $dissolving, $tablet]) => ($edit || $dissolving) && $tablet,
);

let exitInflight: Promise<void> | null = null;

/**
 * Leave list/dashboard edit mode. On mobile/tablet, dissolves the Exit / Finish
 * control first, then clears `$edit` so the tab rail can flow back down.
 * Also clears `initialEdit` (room-devices reorder path).
 *
 * If a prior exit promise is stuck while `$edit` is still true (e.g. interrupted
 * dissolve), abandon it and run a fresh exit so Finish never goes dead.
 */
export function requestExitEditMode(): Promise<void> {
	if (exitInflight) {
		if (get(edit) && !get(editChromeExitDissolving)) {
			exitInflight = null;
		} else {
			return exitInflight;
		}
	}

	exitInflight = (async () => {
		try {
			if (!get(edit)) {
				initialEdit.set(false);
				editChromeExitDissolving.set(false);
				return;
			}

			const animateChrome = get(is_tablet) && !prefersReducedMotion();
			if (animateChrome) {
				editChromeExitDissolving.set(true);
				await new Promise<void>((resolve) => {
					setTimeout(resolve, EDIT_EXIT_DISSOLVE_MS);
				});
			}

			edit.set(false);
			initialEdit.set(false);
		} finally {
			editChromeExitDissolving.set(false);
			exitInflight = null;
		}
	})();

	return exitInflight;
}

/** Synchronous escape hatch — clears edit chrome with no dissolve / inflight gate. */
export function forceExitEditMode(): void {
	exitInflight = null;
	editChromeExitDissolving.set(false);
	edit.set(false);
	initialEdit.set(false);
}
